<template>
  <div>
    <ValidationObserver ref="GoalForm">
      <div
        v-if="showClassifier"
        class="goal-classifier__container"
        :class="{ 'goal-classifier__container--divider': classifierConfirmed }"
      >
        <ValidationProvider
          v-slot="{ errors }"
          :rules="subAreaOptions.length ? 'required' : ''"
          name="la sub área"
        >
          <CustomDropDown
            v-model="model.subArea"
            class="mb-3"
            label="Sub Área:"
            :options="subAreaOptions"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
            :error="errors[0]"
            custom-error-msg="Campo es requerido"
            :disabled="!subAreaOptions.length"
          />
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors }"
          name="las necesidades y/o fortalezas"
          :rules="
            skillsRequired && !model.strenghtsAndNecessities.length
              ? 'required'
              : ''
          "
        >
          <CustomMultiSelect
            label="Fortalezas / Necesidades:"
            :error-message="errors[0]"
            :options="necessityOptions"
            v-model="model.strenghtsAndNecessities"
          />
          <NecessityStrengthGoalList
            v-if="model.strenghtsAndNecessities?.length > 0"
            :list="model.strenghtsAndNecessities"
            @delete="handleDeleteNec($event)"
          />
        </ValidationProvider>

        <!-- paso 1 de 2 -- SOLO cuando twoStepGate (disparo por IA, ver
             GoalsManager.vue/formOpenedViaAi): mientras no se confirme la clasificación, el
             resto del formulario (Término/Actividad/Adecuacidad/Crear Meta) ni siquiera se
             monta -- evita que se vea el alta manual completa (con "Crear Meta" a la vista)
             antes de saber a qué Sub Área/Fortaleza-Necesidad corresponde la Meta. "Continuar"
             valida solo estos dos campos (los de abajo no están registrados todavía en
             ValidationObserver). Es un botón explícito y no un disparo automático al cambiar
             la selección porque Fortalezas/Necesidades es de selección MÚLTIPLE -- no hay
             forma de saber "cuándo terminó" de elegir sin que el usuario lo confirme.

             Con "Añadir Meta" manual (twoStepGate false) nada de este bloque se monta --
             classifierConfirmed ya nace en true (ver data()), así que el panel se ve
             EXACTAMENTE como antes: Sub Área + Fortalezas arriba y el resto del formulario
             visible de una, sin paso intermedio. -->
        <template v-if="twoStepGate">
          <div v-if="!classifierConfirmed" class="goal-classifier__actions">
            <ActionButton
              v-if="canCancel"
              i="fa fa-times-circle"
              class="mr-2"
              variant="outline-danger"
              @click="$emit('cancel')"
            >
              Cancelar
            </ActionButton>
            <ActionButton i="fas fa-arrow-right" variant="success" @click="confirmClassifier">
              Continuar
            </ActionButton>
          </div>
          <div v-else class="goal-classifier__summary">
            <span>
              <b>Sub Área:</b> {{ model.subArea?.name }}
              <template v-if="model.strenghtsAndNecessities.length">
                &nbsp;·&nbsp;<b>Fortaleza/Necesidad:</b> {{ classifierSummaryText }}
              </template>
            </span>
            <!-- oculto mientras suppressManualFields (IA presentando sugerencias) --
                 reclasificar a mitad de esa generación no tiene sentido. -->
            <button
              v-if="!suppressManualFields"
              type="button"
              class="goal-classifier__edit-link"
              @click="classifierConfirmed = false"
            >
              Cambiar
            </button>
          </div>
        </template>
      </div>

      <!-- v-show (no v-if) para suppressManualFields: los campos siguen montados y
           registrados en ValidationObserver (así save() los sigue validando de verdad
           incluso durante un guardado disparado por "Usar esta meta"/inserción masiva, que
           deja aiGoalModal.visible en true todo el guardado) -- solo se ocultan de la
           vista para que "Crear Meta" no conviva con el modal de sugerencias. -->
      <div v-if="classifierConfirmed" v-show="!suppressManualFields">
        <p><b>Añadir Meta:</b></p>

        <div class="row">
          <div class="col-3">
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              name="el término"
            >
              <CustomDropDown
                v-model="model.term"
                label="Término:"
                :options="termOptions"
                fieldkey="id"
                fieldname="name"
                placeholder="Seleccione"
                :error="errors[0]"
                custom-error-msg="Campo es requerido"
              />
            </ValidationProvider>
          </div>

          <div class="col-3">
            <CustomInput
              v-model="model.studentName"
              type="text"
              label="Persona:"
              disabled
            />
          </div>

          <div class="col-3">
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              name="la actividad"
            >
              <CustomInput
                v-model="model.activity"
                type="text"
                label="Actividad:"
                placeholder="Describa actividad (verbo)"
                :error="errors[0]"
                custom-error-msg="Campo es requerido"
              />
            </ValidationProvider>
          </div>

          <div class="col-3">
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              name="la adecuacidad"
            >
              <CustomDropDown
                v-model="model.suitability"
                label="Adecuacidad:"
                :options="suitabilityOptions"
                fieldkey="id"
                fieldname="name"
                placeholder="Seleccione"
                :error="errors[0]"
                custom-error-msg="Campo es requerido"
              />
            </ValidationProvider>
          </div>

          <AdequacyForm
            v-if="showAdequacy"
            v-model="model.adequacyData"
            :adequacy-options="adequacyOptions"
          />
        </div>
      </div>
    </ValidationObserver>

    <div v-if="classifierConfirmed" v-show="!suppressManualFields">
      <Previewer title="Previsualizar meta:" :text="preview" />

      <div class="goal-actions__container">
        <ActionButton
          v-if="canCancel"
          i="fa fa-times-circle"
          class="mr-2"
          variant="outline-danger"
          @click="$emit('cancel')"
        >
          Cancelar
        </ActionButton>
        <ActionButton i="fas fa-check" variant="success" @click="save">
          Crear Meta
        </ActionButton>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import CustomInput from '@/components/common/CustomInput';
import ActionButton from '@/components/common/ActionButton';
import CustomDropDown from '@/components/common/CustomDropDown';
import { BuildGoalDescription } from './common/Helper.js';
import Previewer from './common/Previewer.vue';
import { CustomMultiSelect } from '@/components/common';
import NecessityStrengthGoalList from '../NecessityStrengthGoalList';
import AdequacyForm from './adequacy/AdequacyForm.vue';

export default {
  name: 'GoalForm',
  components: {
    ActionButton,
    ValidationProvider,
    ValidationObserver,
    CustomInput,
    CustomDropDown,
    Previewer,
    CustomMultiSelect,
    NecessityStrengthGoalList,
    AdequacyForm,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        subArea: null,
        strenghtsAndNecessities: [],
        term: null,
        suitability: null,
        activity: '',
        studentName: '',
      }),
    },
    subAreaOptions: {
      type: Array,
      default: () => [],
    },
    necessityOptions: {
      type: Array,
      default: () => [],
    },
    termOptions: {
      type: Array,
      default: () => [],
    },
    suitabilityOptions: {
      type: Array,
      default: () => [],
    },
    showClassifier: {
      type: Boolean,
      default: true,
    },
    canCancel: {
      type: Boolean,
      default: false,
    },
    skillsRequired: {
      type: Boolean,
      default: true,
    },
    adequacyOptions: {
      type: Array,
      default: () => [],
    },
    showAdequacy: {
      type: Boolean,
      default: false,
    },
    // true mientras el consumidor está presentando sugerencias de IA sobre esta
    // clasificación (GoalsManager.vue -- aiGoalModal.visible) -- oculta el resto del
    // formulario (paso 2) aunque classifierConfirmed ya sea true, para que "Crear Meta" no
    // conviva en pantalla con el modal de sugerencias.
    suppressManualFields: {
      type: Boolean,
      default: false,
    },
    // true SOLO cuando este alta la disparó una acción de IA (GoalsManager.vue --
    // formOpenedViaAi) -- habilita el paso 1 de 2 (Sub Área/Fortaleza + "Continuar" antes de
    // convocar al agente). Con "Añadir Meta" manual esto queda en false: el panel se
    // comporta como siempre, sin paso intermedio, aunque showClassifier sea true.
    twoStepGate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // arranca confirmado (sin paso 1) salvo que de verdad haya un paso 1 que mostrar:
      // showClassifier true Y twoStepGate true (disparo por IA). Con "Añadir Meta" manual
      // (twoStepGate false) o con la clasificación ya fija desde afuera (showClassifier
      // false, ej. SubAreaNecessity.vue), nace confirmado y el formulario se ve completo
      // de una, como siempre.
      classifierConfirmed: !(this.showClassifier && this.twoStepGate),
      model: {
        subArea: null,
        strenghtsAndNecessities: [],
        term: null,
        suitability: null,
        activity: '',
        studentName: '',
        // ver el comentario en save() sobre estos dos.
        descriptionOverride: null,
        descriptionAiGenerationLogId: null,
        adequacyData: {
          adequacy: null,
          measure1Amt: null,
          measure2Amt: null,
        },
      },
    };
  },
  methods: {
    // "Continuar" del paso 1 -- valida solo Sub Área/Fortaleza-Necesidad (las únicas
    // ValidationProvider montadas en este punto) antes de revelar el resto del formulario o
    // avisarle al consumidor que ya puede convocar a la IA (ver classifier-confirmed).
    async confirmClassifier() {
      const isValid = await this.$refs.GoalForm.validate();
      if (!isValid) return;

      this.classifierConfirmed = true;
      this.$emit('classifier-confirmed');
    },
    async save() {
      const isValid = await this.$refs.GoalForm.validate();
      if (!isValid) return isValid;

      // "editar antes de usar" (ver aiSuggestionEditMixin.js/GoalsManager.vue-
      // >applyAiGoalSuggestion): si el especialista editó a mano el texto de la tarjeta de
      // sugerencia ANTES de usarla, descriptionOverride trae ese texto completo tal cual y
      // reemplaza a BuildGoalDescription -- si no hubo edición (el caso de siempre, incl. el
      // alta manual normal, donde nunca viene seteado), el cálculo es exactamente el mismo que
      // antes de este cambio. descriptionAiGenerationLogId solo viaja cuando NO hubo edición
      // (ver PeiEngine.AddAcademicAreaGoalAsync/RegisterAppliedAiField -- auditoría de qué
      // corrida de IA originó el texto que quedó guardado tal cual).
      const payload = {
        subAreaId: this.model.subArea?.id,
        strenghtsAndNecessities: this.model.strenghtsAndNecessities,
        description: this.model.descriptionOverride || BuildGoalDescription(this.model),
        descriptionAiGenerationLogId: this.model.descriptionOverride
          ? null
          : (this.model.descriptionAiGenerationLogId || null),
      };

      this.$emit('save', payload);
      this.$refs.GoalForm?.reset();

      return isValid;
    },
    handleDeleteNec(item) {
      if (!item) return;

      this.model.strenghtsAndNecessities.splice(
        this.model.strenghtsAndNecessities.indexOf(item),
        1
      );
    },
  },
  computed: {
    areNecesitiesDisabled() {
      return !!this.subAreaOptions.length && !this.model.subArea?.id;
    },
    preview() {
      return BuildGoalDescription(this.model);
    },
    // resumen del paso 1 ya confirmado (selección múltiple -- se listan todos los nombres,
    // no solo el primero como aiGoalNecessityText en GoalsManager.vue).
    classifierSummaryText() {
      return this.model.strenghtsAndNecessities
        .map((x) => x.name)
        .filter(Boolean)
        .join(', ');
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.model.subArea = newValue.subArea;
        this.model.strenghtsAndNecessities = newValue.strenghtsAndNecessities;
        this.model.term = newValue.term;
        this.model.activity = newValue.activity;
        this.model.studentName = newValue.studentName;
        // AG-05 -- "Meta con Asistente IA": faltaba sincronizar la adecuacidad al recibir
        // el objeto por prop (antes solo se actualizaba vía v-model directo del dropdown,
        // nunca al recibir un `value` nuevo desde afuera). Sin esto, pre-llenar
        // newGoal.suitability desde una sugerencia de IA nunca llegaba al formulario.
        this.model.suitability = newValue.suitability;
        // "editar antes de usar" -- ver el comentario en save().
        this.model.descriptionOverride = newValue.descriptionOverride || null;
        this.model.descriptionAiGenerationLogId = newValue.descriptionAiGenerationLogId || null;
      },
    },
    model: {
      deep: true,
      handler(newValue) {
        const payload = {
          subArea: newValue.subArea,
          strenghtsAndNecessities: newValue.strenghtsAndNecessities,
          term: newValue.term,
          activity: newValue.activity,
          studentName: newValue.studentName,
          suitability: newValue.suitability,
          descriptionOverride: newValue.descriptionOverride,
          descriptionAiGenerationLogId: newValue.descriptionAiGenerationLogId,
        };

        this.$emit('input', payload);
      },
    },
    'model.subArea'() {
      this.model.strenghtsAndNecessities = [];
    },
    // classifierConfirmed solo se calcula UNA vez en data() -- si el panel ya estaba abierto
    // en modo manual (twoStepGate false, classifierConfirmed nace true) y ahí mismo se
    // dispara "Meta con Asistente IA" (twoStepGate pasa a true sin desmontar este mismo
    // GoalForm), sin este watcher classifierConfirmed se quedaba en true: quedaba el fondo
    // violeta puesto pero el resto del formulario manual ("Añadir Meta:", Previsualizar,
    // Crear Meta) seguía visible en vez de volver al paso 1. Recalcula con la misma fórmula
    // de data() cada vez que cambia, para que el paso 1 se reinicie igual que si el
    // formulario se hubiera montado de cero.
    twoStepGate() {
      this.classifierConfirmed = !(this.showClassifier && this.twoStepGate);
    },
  },
};
</script>

<style scoped>
.goal-classifier__container {
}

/* NecessityStrengthGoalList (lista "1. Necesidad"/"2. Necesidad") trae su propio
   background-color: white fijo -- dentro de .goal-new-form__container (fondo lila, disparo
   por IA) esa caja blanca cortaba el fondo en dos en vez de verse como un solo panel. Se
   apaga acá, sin tocar el componente compartido (se usa en más lugares que no llevan fondo
   de color), así que en cualquier fondo blanco normal (alta manual) se ve exactamente igual
   que antes. */
.goal-classifier__container ::v-deep .all-necessities__container {
  background-color: transparent;
}

/* separador hacia el paso 2 (Término/Actividad/etc, o el resto del alta manual) -- solo
   tiene sentido mientras haya algo debajo que separar (classifierConfirmed true). Con el
   paso 1 todavía sin confirmar (Cancelar/Continuar a la vista) no hay nada debajo dentro de
   este mismo formulario -- dejar la línea igual quedaba pegada contra el borde morado
   exterior del contenedor (.goal-new-form__container), como si fueran dos cajas encimadas. */
.goal-classifier__container--divider {
  border-bottom: 2px solid #e7edea;
  margin-bottom: 30px;
}

.goal-classifier__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  /* el dropdown de Fortalezas / Necesidades (CustomMultiSelect) no trae margen propio
     abajo -- sin este margin-top, Cancelar/Continuar quedaban pegados justo debajo de su
     borde. */
  margin-top: 20px;
}

.goal-classifier__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 16px;
}

.goal-classifier__edit-link {
  background: none;
  border: none;
  padding: 0;
  color: #11291b;
  text-decoration: underline;
  font-size: 14px;
  cursor: pointer;
}

.goal-actions__container {
  display: flex;
  justify-content: flex-end;
}
</style>
